import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
  ColorSlide,
  ColorViewer as ColorViewerData,
} from "@roof-hawk/core";
import { lockBodyScroll } from "../../components/ui";
import { WORDMARK_ON_DARK } from "../../logo";

/** How far a finger has to travel sideways before it counts as a swipe. */
const SWIPE_PX = 45;

/**
 * A swipeable viewer of finished roofs, one colour per slide, opened over the
 * proposal from a colour card.
 *
 * Built from the Standing Seam design handoff Jonathon supplied on 2026-09-28:
 * a crossfading stage, a counter, arrows that wrap round, a strip of thumbnails
 * that keeps the current one centred, and the colour's name across the bottom.
 * The slides are data (`ColorViewer` in packages/core), so the Ag Panel and the
 * R Panel can have one too without another component.
 *
 * Always on black, so it draws only the on-dark wordmark: the viewer has no
 * light theme for the other one to be right in.
 *
 * A viewer with a `grid` (siding, from the Parkview handoff of 2026-09-30)
 * draws its photographs to fill the frame, puts a chip of the colour beside
 * its name, and ends on a slide of every colour as a swatch rather than on a
 * chart image. Tapping a swatch goes to that colour's photograph.
 */
export function ColorViewer({
  viewer,
  onClose,
}: {
  viewer: ColorViewerData;
  onClose: () => void;
}) {
  const { slides, grid } = viewer;
  /* The grid, when there is one, is the slide after the last photograph. */
  const total = slides.length + (grid ? 1 : 0);
  const [index, setIndex] = useState(0);
  const wrap = useCallback(
    (n: number) => ((n % total) + total) % total,
    [total],
  );
  const go = useCallback((to: number) => setIndex(wrap(to)), [wrap]);
  /* From whatever is current when it lands, so two quick presses are two. */
  const step = useCallback(
    (by: number) => setIndex((was) => wrap(was + by)),
    [wrap],
  );
  const current = slides[index];
  const onGrid = grid != null && index === slides.length;
  /*
   * The slide showing and the one either side of it load now; the rest wait.
   * Without the neighbours an arrow crossfades into an empty frame while the
   * photograph arrives.
   */
  const near = (n: number) =>
    n === index || n === wrap(index + 1) || n === wrap(index - 1);

  /*
   * The keys are the viewer's only while it is open. It covers the whole
   * page, so nothing under it is waiting for an arrow key, and the listener
   * goes when it closes rather than taking the proposal's keys with it.
   *
   * It listens in the capture phase and stops the event there, because the
   * office opens the proposal inside a `Modal` (`components/ui.tsx`) that
   * closes itself on Escape from a listener of its own on the same document.
   * Without this, one Escape shut the viewer and the preview behind it. Only
   * the viewer's three keys are stopped; every other key goes on as before.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
      else return;
      e.stopImmediatePropagation();
      e.stopPropagation();
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () =>
      document.removeEventListener("keydown", onKey, { capture: true });
  }, [step, onClose]);

  useEffect(lockBodyScroll, []);

  /* Focus comes in with the viewer and goes back to the card that opened it. */
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => opener?.focus();
  }, []);

  /* The current thumbnail, centred in the strip whenever the slide changes. */
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = stripRef.current;
    const thumb = strip?.children[index] as HTMLElement | undefined;
    if (!strip || !thumb) return;
    const stripBox = strip.getBoundingClientRect();
    const thumbBox = thumb.getBoundingClientRect();
    strip.scrollLeft +=
      thumbBox.left - stripBox.left - (stripBox.width - thumbBox.width) / 2;
  }, [index]);

  const touchX = useRef<number | null>(null);

  return createPortal(
    <div
      className="pdoc-cv"
      role="dialog"
      aria-modal="true"
      aria-label={`${viewer.title}: ${viewer.eyebrow}`}
    >
      <div className="pdoc-cv-wrap">
        <header className="pdoc-cv-head">
          <div className="pdoc-cv-titles">
            <span className="pdoc-cv-eyebrow">{viewer.eyebrow}</span>
            <span className="pdoc-cv-title">{viewer.title}</span>
          </div>
          <div className="pdoc-cv-head-end">
            <img className="pdoc-cv-logo" src={WORDMARK_ON_DARK} alt="" />
            <button
              ref={closeRef}
              type="button"
              className="pdoc-cv-round pdoc-cv-close"
              aria-label="Close colors"
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </header>

        <div
          className="pdoc-cv-stage"
          onTouchStart={(e) => {
            touchX.current = e.changedTouches[0]?.clientX ?? null;
          }}
          onTouchEnd={(e) => {
            const start = touchX.current;
            touchX.current = null;
            const end = e.changedTouches[0]?.clientX;
            if (start == null || end == null) return;
            const dx = end - start;
            if (Math.abs(dx) > SWIPE_PX) step(dx < 0 ? 1 : -1);
          }}
        >
          {slides.map((slide, n) => (
            <div
              key={slide.src}
              className={`pdoc-cv-slide${grid ? " fill" : ""}${
                n === index ? " on" : ""
              }`}
              style={slide.hex ? { backgroundColor: slide.hex } : undefined}
              aria-hidden={n !== index}
            >
              <img
                className={slide.chart ? "chart" : undefined}
                src={slide.src}
                alt={slide.name}
                loading={near(n) ? "eager" : "lazy"}
              />
            </div>
          ))}

          {grid ? (
            <SwatchGrid grid={grid} slides={slides} on={onGrid} onPick={go} />
          ) : null}

          <div className="pdoc-cv-count">
            <b>{index + 1}</b>
            <span>/</span>
            <span>{total}</span>
          </div>

          <button
            type="button"
            className="pdoc-cv-round pdoc-cv-arrow prev"
            aria-label="Previous"
            onClick={() => step(-1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            className="pdoc-cv-round pdoc-cv-arrow next"
            aria-label="Next"
            onClick={() => step(1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {onGrid ? null : <Nameplate slide={current} chip={grid != null} />}
        </div>

        <div className="pdoc-cv-strip" ref={stripRef}>
          {slides.map((slide, n) => (
            <button
              key={slide.src}
              type="button"
              className={`pdoc-cv-thumb${n === index ? " on" : ""}${
                slide.chart ? " chart" : ""
              }${grid ? " fill" : ""}`}
              style={slide.hex ? { backgroundColor: slide.hex } : undefined}
              aria-label={`View ${slide.name}`}
              aria-current={n === index}
              onClick={() => go(n)}
            >
              <img src={slide.src} alt="" loading="lazy" />
            </button>
          ))}
          {grid ? (
            <button
              type="button"
              className={`pdoc-cv-thumb all${onGrid ? " on" : ""}`}
              aria-label="View all colors"
              aria-current={onGrid}
              onClick={() => go(slides.length)}
            >
              All
            </button>
          ) : null}
        </div>

        <div className="pdoc-cv-hint">
          Swipe, tap, or use <span>←</span> <span>→</span> &nbsp;•&nbsp; last
          slide shows {grid ? "all colors" : "the full color chart"}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The colour's name across the bottom, with its chip when it has one. */
function Nameplate({
  slide,
  chip,
}: {
  slide: ColorSlide | undefined;
  chip: boolean;
}) {
  return (
    <div className={`pdoc-cv-plate${chip ? " chip" : ""}`} aria-live="polite">
      {chip && slide?.hex ? (
        <span
          className="pdoc-cv-swatch"
          style={{ backgroundColor: slide.hex }}
          aria-hidden="true"
        />
      ) : null}
      <div className="pdoc-cv-name">{slide?.name}</div>
      {slide?.premium ? (
        <span className="pdoc-cv-premium">★ Premium Color</span>
      ) : null}
    </div>
  );
}

/**
 * Every colour as a swatch, as the last slide. A swatch is a button that goes
 * to its colour's photograph, and none is reachable by Tab while the slide is
 * hidden.
 */
function SwatchGrid({
  grid,
  slides,
  on,
  onPick,
}: {
  grid: NonNullable<ColorViewerData["grid"]>;
  slides: readonly ColorSlide[];
  on: boolean;
  onPick: (n: number) => void;
}) {
  return (
    <div
      className={`pdoc-cv-slide pdoc-cv-grid${on ? " on" : ""}`}
      aria-hidden={!on}
    >
      <div className="pdoc-cv-grid-panel">
        <div className="pdoc-cv-grid-head">{grid.heading}</div>
        <div className="pdoc-cv-chips">
          {slides.map((slide, n) => (
            <button
              key={slide.src}
              type="button"
              className="pdoc-cv-chip"
              tabIndex={on ? 0 : -1}
              onClick={() => onPick(n)}
            >
              <span
                className="sw"
                style={{ backgroundColor: slide.hex }}
                aria-hidden="true"
              />
              <span className="nm">{slide.name}</span>
            </button>
          ))}
        </div>
        <p className="pdoc-cv-disclaimer">{grid.disclaimer}</p>
      </div>
    </div>
  );
}
